
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  handleInput() {
    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template
      .replace('[current]', currentLength.toString())
      .replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define(
  'product-custom-property-component',
  ProductCustomProperty
);


// ==================================================
// EXISTING NAME / DATE PERSONALIZATION LOGIC
// ==================================================

const NAME_DATE_SELECTOR =
  '[data-name-date-conditional="true"]';

const PERSONALIZATION_OPTION_NAMES = [
  'Personalization',
  'Product Type',
];

const PERSONALIZED_VALUES = [
  'Personalized – Name & Date',
  'Personalized - Name & Date',
  'Customization: Name & Date',
];


function getSelectedPersonalizationOption() {
  const radios = document.querySelectorAll(
    'input[type="radio"][data-option-name]'
  );

  return Array.from(radios).find((radio) => {
    const optionName = radio.dataset.optionName?.trim();

    return (
      PERSONALIZATION_OPTION_NAMES.includes(optionName) &&
      radio.checked
    );
  });
}


function syncNameDateProperties() {
  const selectedOption = getSelectedPersonalizationOption();

  // If this template is used on a product without the relevant
  // personalization selector, preserve the existing default behavior
  // and show the custom-property fields.
  const customizationSelected =
    !selectedOption ||
    PERSONALIZED_VALUES.includes(selectedOption.value.trim());

  document.querySelectorAll(NAME_DATE_SELECTOR).forEach((marker) => {
    const component = marker.closest(
      'product-custom-property-component'
    );

    const input = component?.querySelector('input, textarea');

    if (!component || !input) return;

    const propertyKey =
      marker.dataset.propertyKey?.trim().toLowerCase();

    const isNameField = propertyKey === 'name';

    component.style.display = customizationSelected ? '' : 'none';

    input.disabled = !customizationSelected;

    // Name is required only when personalization is selected.
    // Date remains optional.
    input.required = customizationSelected && isNameField;

    if (input.required) {
      input.setAttribute('aria-required', 'true');
    } else {
      input.removeAttribute('aria-required');
    }

    // Clear hidden values so stale personalization cannot accidentally
    // be submitted when the customer switches to No Personalization.
    if (!customizationSelected) {
      input.value = '';
      input.dispatchEvent(
        new Event('input', { bubbles: true })
      );
    }
  });
}


document.addEventListener('change', (event) => {
  if (
    event.target.matches(
      'input[type="radio"][data-option-name]'
    )
  ) {
    const optionName =
      event.target.dataset.optionName?.trim();

    if (
      PERSONALIZATION_OPTION_NAMES.includes(optionName)
    ) {
      syncNameDateProperties();
    }
  }
});


window.addEventListener(
  'pageshow',
  syncNameDateProperties
);

document.addEventListener(
  'DOMContentLoaded',
  syncNameDateProperties
);

requestAnimationFrame(syncNameDateProperties);
